import { moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component,HostListener,Inject,OnDestroy } from '@angular/core';
import { FormArray,FormControl,FormGroup,FormsModule,ReactiveFormsModule,Validators,ɵFormGroupValue } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyDialogConfig as MatDialogConfig,MatLegacyDialogModule as MatDialogModule,MatLegacyDialogRef as MatDialogRef,MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { Permissions } from '@dave/types';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import moment,{ Moment } from 'moment';
import { combineLatest,firstValueFrom,Observable,of,Subject,Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ClockInEntity,ClockInTypes } from '../../../dave-data-module/entities/clock-in.entity';
import { CommissionEntity } from '../../../dave-data-module/entities/commission.entity';
import { EmployeeEntity } from '../../../dave-data-module/entities/employee.entity';
import { WorkDayEntity } from '../../../dave-data-module/entities/work-day.entity';
import { DaveMutationCreateClockInArgs } from '../../../dave-data-module/graphql-types';
import { CommissionResolver } from '../../../dave-data-module/guards/commission.resolver';
import { EmployeeResolver } from '../../../dave-data-module/guards/employee.resolver';
import { EntityRoleResolver } from '../../../dave-data-module/guards/entity-role.resolver';
import { UserToCommissionResolver } from '../../../dave-data-module/guards/user-to-commission.resolver';
import { ComponentCanDeactivate,PENDING_CHANGES_DEFAULT_MESSAGE } from '../../../dave-data-module/guards/pending-changes.guard';
import { FrontendDate } from '../../../dave-data-module/helper/backend-frontend-conversion.helper';
import { getFetched$ } from '../../../dave-data-module/helper/helper';
import { ClockInDataService } from '../../../dave-data-module/services/clockin-data.service';
import { State } from '../../../dave-data-module/State';
import { getClockInTypeDictionary,getClockInTypesFetched } from '../../../dave-data-module/State/selectors/clock-in-type.selectors';
import {
getCommissionDictionary,getCommissionsActive,
getCommissionsFetched
} from '../../../dave-data-module/State/selectors/commission.selector';
import { getEmployeeByUserId,getEmployeesFetched,getEmployeesSortedByNameStartWithMe } from '../../../dave-data-module/State/selectors/employees.selectors';
import { getEntityRole,getEntityRoleFetched } from '../../../dave-data-module/State/selectors/entity-role.selector';
import { getUserToCommissionFetched,getUserToCommissions } from '../../../dave-data-module/State/selectors/user-to-commission.selector';
import { getPartnerMainOffice,getPartnerOfficesFetched } from '../../../dave-data-module/State/selectors/partners.selectors';
import { getUser } from '../../../dave-data-module/State/selectors/users.selectors';
import { getWorkDays } from '../../../dave-data-module/State/selectors/work-day.selectors';
import { DaveLoadingModule } from '../../../dave-loading/dave-loading.module';
import { TimeTrackerService } from '../../../dave-time-tracker/time-tracker.service';
import { AppButtonModule } from '../../../dave-utils-module/app-button-module/app-button.module';
import { PermissionService } from '../../../dave-utils-module/dave-shared-components-module/services/permission.service';
import { SelectSearchOption } from '../../../dave-utils-module/select-search/components/select-search/select-search.component';
import { SelectSearchModule } from '../../../dave-utils-module/select-search/select-search.module';
import { appMatDialogDefaultConfig,DEFAULT_START_TIME,getMinimumBreakHours,isNotNullOrUndefined,sameDay,sortClockIns } from '../../../helper/helper';
import { ClockInHelperService } from '../../../services/clock-in-helper.service';
import { CustomLabelService } from '../../../services/custom-label/custom-label.service';
import { LoadingService } from '../../../services/loading.service';
import { TimePickerSelectComponent } from '../../../time-picker-select/time-picker-select.component';
import { EndTimeDirective } from '../../../time-span-picker-select/end-time.directive';
import { StartTimeDirective } from '../../../time-span-picker-select/start-time.directive';
import { TimeSpanPickerSelectComponent } from '../../../time-span-picker-select/time-span-picker-select.component';
import { WorkDayResolver } from '../../../dave-data-module/guards/work-day.resolver';
import { getTimeTrackableEmployeesFromCommission } from '../../time-tracker.helper';

type additionalTimeSpanForm = {
    startTime: FormControl<string>;
    endTime: FormControl<string>;
    clockInTypeId: FormControl<number>;
    commissionId: FormControl<SelectSearchOption<CommissionEntity>>;
    note: FormControl<string>;
};
export interface DayTimeTrackerComponentDialogData {
    date?: Date;
    defaultCommissionId?: number;
}

@Component({
    selector: 'app-day-time-tracker',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        AppButtonModule,
        MatInputModule,
        MatDatepickerModule,
        EndTimeDirective,
        FormsModule,
        StartTimeDirective,
        TimePickerSelectComponent,
        TimeSpanPickerSelectComponent,
        MatButtonModule,
        FontAwesomeModule,
        ReactiveFormsModule,
        MatExpansionModule,
        MatSelectModule,
        SelectSearchModule,
        DaveLoadingModule,
    ],
    templateUrl: './day-time-tracker.component.html',
    styleUrls: ['./day-time-tracker.component.scss'],
})
export class DayTimeTrackerComponent implements OnDestroy, ComponentCanDeactivate {
    public static DefaultConfig: MatDialogConfig = {
        ...appMatDialogDefaultConfig,
        disableClose: true,
    };
    private onDestroy = new Subject<void>();
    protected expandedIndex: number = null;
    protected form: FormGroup<{
        date: FormControl<moment.Moment | null>;
        commission: FormControl<SelectSearchOption<CommissionEntity> | null>;
        employee: FormControl<EmployeeEntity | null>;
        startTime: FormControl<string | null>;
        endTime: FormControl<string | null>;
        additionalTimeSpans: FormArray<FormGroup<additionalTimeSpanForm>>;
    }>;
    protected clockInTypes$ = (requiredId: number) => this.timeTrackerService.clockInTypesWithoutMainAndDisabledBySettings$(requiredId);
    protected commissions$ = getFetched$(this.store, getCommissionsFetched, getCommissionsActive, this.commissionResolver);
    protected allCommissions$ = getFetched$(this.store, getCommissionsFetched, getCommissionDictionary, this.commissionResolver);
    private employee$ = this.store.select(getUser).pipe(
        filter(isNotNullOrUndefined),
        map((u) => u.Id),
        distinctUntilChanged(),
        switchMap((userId) => getFetched$(this.store, getEmployeesFetched, getEmployeeByUserId({ userId }), this.employeeResolver)),
        shareReplay({ bufferSize: 1, refCount: true }),
    );
    protected hasPermissions$ = this.employee$.pipe(
        map((employee) => employee && employee.PermissionsFromEntityRoles.includes(Permissions.EditClockIn) /*&& employee.PermissionsFromEntityRoles.includes(Permissions.CreateClockIn)*/),
        shareReplay({ refCount: true, bufferSize: 1 }),
    );
    private subs: Subscription[] = [];
    private workDays$ = this.store.select(getWorkDays).pipe(map((wd) => wd.filter((w) => !w.DeletedAt)));
    private workdaysFromDate$: Observable<WorkDayEntity[]>;
    private clockInsFromDate$: Observable<ClockInEntity[]>;
    // private clockInsFromEmployee$ = this.employee$.pipe(
    //     map((e) => e.Id),
    //     distinctUntilChanged(),
    //     switchMap((employeeId) => this.store.select(getClockInsWithoutDeleted).pipe(map((clockIns) => clockIns.filter((c) => c.EmployeeId === employeeId)))),
    // );

    protected commissionOptions$: Observable<CommissionEntity[]>;
    protected employeeOptions$: Observable<EmployeeEntity[]>;
    private selectedEmployee$: Observable<EmployeeEntity | null>;
    protected compareById = (a: { Id: number } | null, b: { Id: number } | null) => a?.Id === b?.Id;

    private mainPartnerOffice$ = getFetched$(this.store, getPartnerOfficesFetched, getPartnerMainOffice).pipe(shareReplay({ bufferSize: 1, refCount: true }));
    private defaultStartTimeFromPartner$ = this.mainPartnerOffice$.pipe(map((po) => po?.AdditionalData?.defaultWorkTimeBegin || DEFAULT_START_TIME));
    private defaultClockInTypeFromPartner$ = this.mainPartnerOffice$.pipe(map((po) => po?.AdditionalData?.defaultClockInTypeId));
    private calculateDefaultEndClockInFromEmployeeWorkingHours$ = this.mainPartnerOffice$.pipe(map((po) => !!po?.AdditionalData?.calculateDefaultEndClockInFromEmployeeWorkingHours));
    public Loaded$: Observable<boolean>;
    constructor(
        protected ls: LoadingService,
        private store: Store<State>,
        private commissionResolver: CommissionResolver,
        private employeeResolver: EmployeeResolver,
        workDayResolver: WorkDayResolver,
        userToCommissionResolver: UserToCommissionResolver,
        entityRoleResolver: EntityRoleResolver,
        protected ps: PermissionService,
        protected cls: CustomLabelService,
        private timeTrackerService: TimeTrackerService,
        private dialogRef: MatDialogRef<DayTimeTrackerComponent>,
        private clockInHelperService: ClockInHelperService,
        private clockInDataService: ClockInDataService,
        @Inject(MAT_DIALOG_DATA) dialogData: DayTimeTrackerComponentDialogData,
    ) {
        if (this.dialogRef) {
            this.subs.push(
                this.dialogRef.backdropClick().subscribe(() => {
                    this.closeDialog();
                }),
            );
        }
        this.form = new FormGroup({
            date: new FormControl<Moment>(dialogData?.date ? moment(dialogData.date) : moment(), Validators.required),
            commission: new FormControl<SelectSearchOption<CommissionEntity>>(null),
            employee: new FormControl<EmployeeEntity>(null, Validators.required),
            startTime: new FormControl<string>(DEFAULT_START_TIME, Validators.required),
            endTime: new FormControl<string>(null),
            additionalTimeSpans: new FormArray<FormGroup<additionalTimeSpanForm>>([]),
        });
        if (dialogData?.defaultCommissionId) {
            firstValueFrom(this.allCommissions$).then((commissions) => {
                if (!this.form.value.commission && commissions[dialogData.defaultCommissionId]) {
                    this.form.controls.commission.setValue(commissions[dialogData.defaultCommissionId]);
                }
            });
        }
        this.employeeOptions$ = combineLatest([
            this.form.controls.commission.valueChanges.pipe(
                startWith(null),
                map(val => val?.Id),
                distinctUntilChanged(),
            ),
            this.store.select(getEmployeesSortedByNameStartWithMe).pipe(filter(isNotNullOrUndefined)),
            getFetched$(this.store, getEntityRoleFetched, getEntityRole, entityRoleResolver),
            getFetched$(this.store, getUserToCommissionFetched, getUserToCommissions, userToCommissionResolver),
            this.employee$,
        ]).pipe(
            map(([commissionId, employees, entityRoles, userToCommissions, me]) => {
                const commissionEmployees = getTimeTrackableEmployeesFromCommission(commissionId, employees, entityRoles, userToCommissions);
                // man selbst ist immer auswählbar, auch wenn man nicht dem Auftrag zugewiesen ist
                return me && !commissionEmployees.some((e) => e.Id === me.Id) ? [me, ...commissionEmployees] : commissionEmployees;
            }),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
        // ist der ausgewählte Mitarbeiter (z.B. nach Auftragswechsel) nicht mehr auswählbar, wird man selbst vorausgewählt
        this.subs.push(
            combineLatest([this.employeeOptions$, this.employee$]).subscribe(([options, me]) => {
                if (me && !options.some((e) => e.Id === this.form.value.employee?.Id)) {
                    this.form.controls.employee.setValue(me);
                }
            }),
        );
        this.selectedEmployee$ = this.form.controls.employee.valueChanges.pipe(
            startWith(null),
            map(() => this.form.value.employee || null),
            distinctUntilChanged((a, b) => a?.Id === b?.Id),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
        this.workdaysFromDate$ = combineLatest([
            this.form.controls.date.valueChanges.pipe(
                startWith(null),
                map(() => this.form.value.date),
            ),
            this.selectedEmployee$.pipe(
                map((e) => e?.Id),
                distinctUntilChanged(),
                switchMap((employeeId) => (employeeId ? this.workDays$.pipe(map((workDays) => workDays.filter((w) => w.EmployeeId === employeeId))) : of([] as WorkDayEntity[]))),
            ),
        ]).pipe(
            takeUntil(this.onDestroy),
            map(([date, workdays]) => date ? workdays.filter((c) => sameDay(c.Timestamp, date.toDate())) : [] as WorkDayEntity[]),
            shareReplay(1),
        );
        this.clockInsFromDate$ = this.workdaysFromDate$.pipe(
            switchMap((wds) => (wds.length ? this.clockInDataService.getByWorkdays$(wds.map((w) => w.Id)) : of([]))),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
        this.commissionOptions$ = combineLatest([this.commissions$, this.clockInsFromDate$, this.allCommissions$]).pipe(
            map(([activeCommissions, clockIns, allCommissions]) => {
                const selectedClockIns = clockIns;
                const clockInCommissionIds = new Set(selectedClockIns.map((ci) => ci.CommissionId).filter((id) => id !== null));

                if (clockInCommissionIds.size === 0) {
                    return activeCommissions;
                }

                const activeCommissionIds = new Set(activeCommissions.map((c) => c.Id));
                const additionalCommissions = [...clockInCommissionIds].map((cId) => allCommissions[cId]).filter((c) => !activeCommissionIds.has(c.Id)) ?? [];

                return [...additionalCommissions, ...activeCommissions];
            }),
        );
        this.subs.push(
            combineLatest([
                this.form.controls.date.valueChanges.pipe(
                    startWith(null),
                    map(() => this.form.value.date),
                ),
                this.workdaysFromDate$,
                this.clockInsFromDate$,
                this.timeTrackerService.mainClockInType$,
                getFetched$(this.store, getClockInTypesFetched, getClockInTypeDictionary),
                this.defaultStartTimeFromPartner$,
                this.selectedEmployee$,
                this.calculateDefaultEndClockInFromEmployeeWorkingHours$,
                getFetched$(this.store, getCommissionsFetched, getCommissionDictionary),
            ]).subscribe(([date, selectedWorkdays, clockIns, mainClockInType, clockInTypes, defaultStartTime, employee, calculateDefaultEnd, commissionDictionary]) => {
                if (date) {
                    // const selectedWorkdays = workdays.filter((c) => sameDay(c.Timestamp, date.toDate()));
                    const selectedWorkday = selectedWorkdays.length === 1 && selectedWorkdays[0];
                    this.form.controls.additionalTimeSpans.clear();
                    console.log({ defaultStartTime });
                    this.form.controls.startTime.setValue(defaultStartTime);
                    let endTime: string | null = null;
                    if (calculateDefaultEnd && employee?.WeeklyWorkingHours?.length === 7) {
                        const dow = date.day(); // 0-6 0=Sunday
                        const workingHours = employee.WeeklyWorkingHours.slice();
                        moveItemInArray(workingHours, 6, 0);
                        const hours = workingHours[dow] || null;
                        if (hours && defaultStartTime.split(':').length === 2) {
                            const sh = +defaultStartTime.split(':')[0];
                            const sm = +defaultStartTime.split(':')[1];
                            const hoursToAdd = (hours + getMinimumBreakHours(hours)) * 60 * 60 * 1000;
                            const date = new Date(0, 0, 0, sh, sm);
                            date.setTime(date.getTime() + hoursToAdd);
                            endTime = date.getHours() + ':' + date.getMinutes();
                        }
                    }
                    this.form.controls.endTime.setValue(endTime);

                    if (selectedWorkday) {
                        const selectedClockIns = clockIns.filter((c) => c.WorkDayId === selectedWorkday.Id).sort(sortClockIns(mainClockInType.Id));
                        const startClockIn = selectedClockIns.find((c) => c.ClockInTypeId === mainClockInType.Id && c.Type === ClockInTypes.Start);
                        if (startClockIn) {
                            this.setFormByClockIn(
                                this.form.controls,
                                startClockIn,
                                selectedClockIns.find((c) => c.ClockInTypeId === startClockIn.ClockInTypeId && c.Type === ClockInTypes.End),
                            );
                        }

                        selectedClockIns.forEach((ci, i, arr) => {
                            if (ci.ClockInTypeId !== mainClockInType.Id && ci.Type === ClockInTypes.Start) {
                                let endClockIn: ClockInEntity;

                                for (let k = i + 1; k < arr.length; k++) {
                                    if ((arr[k].ClockInTypeId === ci.ClockInTypeId && arr[k].Type === ClockInTypes.End) || (arr[k].Type === ClockInTypes.Start && clockInTypes[arr[k].ClockInTypeId].IsWorkingTime)) {
                                        endClockIn = arr[k];
                                        break;
                                    }
                                }

                                this.addForm({
                                    startTime: ci.TimeStamp.getHours().toString().padStart(2, '0') + ':' + ci.TimeStamp.getMinutes().toString().padStart(2, '0'),
                                    endTime: endClockIn ? endClockIn.TimeStamp.getHours().toString().padStart(2, '0') + ':' + endClockIn.TimeStamp.getMinutes().toString().padStart(2, '0') : this.form.value.endTime,
                                    note: ci.WorkDescription,
                                    commissionId: commissionDictionary[ci?.CommissionId],
                                    clockInTypeId: ci.ClockInTypeId,
                                });
                            }
                        });
                    }
                }
            }),
        );
        this.Loaded$ = combineLatest([workDayResolver.resolve(), userToCommissionResolver.resolve(), entityRoleResolver.resolve()]).pipe(
            map((loaded) => loaded.every((l) => l)),
            take(1),
            map(() => true),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
    }
    public setFormByClockIn(forms: { startTime: FormControl<string>; endTime: FormControl<string> }, startClockIn: ClockInEntity, endClockIn?: ClockInEntity) {
        forms.startTime.setValue(startClockIn.TimeStamp.getHours().toString().padStart(2, '0') + ':' + startClockIn.TimeStamp.getMinutes().toString().padStart(2, '0'));
        forms.endTime.setValue(endClockIn ? endClockIn.TimeStamp.getHours().toString().padStart(2, '0') + ':' + endClockIn.TimeStamp.getMinutes().toString().padStart(2, '0') : null);
    }
    ngOnDestroy(): void {
        this.onDestroy.next();
        this.subs.forEach((s) => s.unsubscribe());
    }
    protected addForm(defaults?: ɵFormGroupValue<additionalTimeSpanForm>) {
        this.form.controls.additionalTimeSpans.push(
            new FormGroup<additionalTimeSpanForm>({
                startTime: new FormControl<string>(defaults?.startTime !== undefined ? defaults.startTime : null, Validators.required),
                endTime: new FormControl<string>(defaults?.endTime !== undefined ? defaults.endTime : null, Validators.required),
                clockInTypeId: new FormControl<number>(defaults?.clockInTypeId !== undefined ? defaults.clockInTypeId : null, Validators.required),
                commissionId: new FormControl<SelectSearchOption<CommissionEntity>>(defaults?.commissionId ? defaults.commissionId : null),
                note: new FormControl<string>(defaults?.note !== undefined ? defaults.note : null),
            }),
        );
    }
    protected submit() {
        if (this.ls.isAlreadyLoading('app-day-time-tracker-submit')) {
            console.error('isAlreadyLoading: app-day-time-tracker-submit');
            return;
        }
        this.ls.startLoading('app-day-time-tracker-submit');

        firstValueFrom(combineLatest([this.clockInsFromDate$, this.timeTrackerService.mainClockInType$])).then(([clockIns, mainClockInType]) => {
            const employee = this.form.value.employee;
            const clockInsToDelete = clockIns;
            const clockInsToAdd: DaveMutationCreateClockInArgs[] = [
                {
                    type: ClockInTypes.Start,
                    timeStamp: FrontendDate(new Date(this.form.value.date.year(), this.form.value.date.month(), this.form.value.date.date(), +this.form.value.startTime.split(':')[0], +this.form.value.startTime.split(':')[1], 0, 0)),
                    clockInTypeId: mainClockInType.Id,
                    employeeId: employee.Id,
                },
            ];
            if (this.form.value.endTime) {
                clockInsToAdd.push({
                    type: ClockInTypes.End,
                    timeStamp: FrontendDate(new Date(this.form.value.date.year(), this.form.value.date.month(), this.form.value.date.date(), +this.form.value.endTime.split(':')[0], +this.form.value.endTime.split(':')[1], 0, 0)),
                    clockInTypeId: mainClockInType.Id,
                    employeeId: employee.Id,
                });
            }
            this.form.controls.additionalTimeSpans.value.forEach((f) => {
                clockInsToAdd.push(
                    {
                        type: ClockInTypes.Start,
                        timeStamp: FrontendDate(new Date(this.form.value.date.year(), this.form.value.date.month(), this.form.value.date.date(), +f.startTime.split(':')[0], +f.startTime.split(':')[1], 0, 0)),
                        clockInTypeId: f.clockInTypeId,
                        commissionId: f.commissionId?.Id,
                        workDescription: f.note,
                        employeeId: employee.Id,
                    },
                    {
                        type: ClockInTypes.End,
                        timeStamp: FrontendDate(new Date(this.form.value.date.year(), this.form.value.date.month(), this.form.value.date.date(), +f.endTime.split(':')[0], +f.endTime.split(':')[1], 0, 0)),
                        clockInTypeId: f.clockInTypeId,
                        commissionId: f.commissionId?.Id,
                        workDescription: f.note,
                        employeeId: employee.Id,
                    },
                );
            });
            this.clockInHelperService
                .dispatchClockInActions(
                    clockInsToAdd,
                    [],
                    clockInsToDelete.map((c) => ({ id: c.Id })),
                )
                .subscribe((progress) => {
                    if (progress.loaded >= progress.total) {
                        // darf hier nicht false gesetzt werden, da das sofort passiert und deshalb ein Doppelklick möglich ist
                        setTimeout(() => this.ls.endLoading('app-day-time-tracker-submit'), 100);
                    }
                    this.dialogRef?.close();
                });
            // let error = false;
            // this.actions$
            //     .pipe(ofType(BaseActionTypes.ErrorAction, ClockInActionTypes.UpdateMany, ClockInActionTypes.AddClockInsSuccess), take(clockInsToDelete.length + 1))
            //     .pipe(
            //         tap((action) => {
            //             if (action.type === BaseActionTypes.ErrorAction.type) {
            //                 error = true;
            //             }
            //         }),
            //         last(),
            //     )
            //     .subscribe(() => {
            //         this.ls.endLoading('app-day-time-tracker-submit');
            //         if (!error) {
            //             this.dialogRef?.close();
            //         }
            //     });
            // clockInsToDelete.forEach((c) => this.store.dispatch(ClockInActionTypes.DeleteClockIn({ Payload: { id: c.Id } })));
            // this.store.dispatch(ClockInActionTypes.AddClockIns({ Payload: clockInsToAdd }));
        });
    }

    async onAddFormClick() {
        const clockInTypeId = await firstValueFrom(this.defaultClockInTypeFromPartner$);
        this.addForm({
            startTime: this.form.controls.additionalTimeSpans.controls.length === 0 ? this.form.value.startTime : this.form.controls.additionalTimeSpans.controls[this.form.controls.additionalTimeSpans.length - 1].value.endTime,
            endTime: this.form.value.endTime,
            clockInTypeId: clockInTypeId || null,
            commissionId: this.form.value.commission || null,
        });
        this.expandedIndex = this.form.controls.additionalTimeSpans.controls.length - 1;
    }

    onDeleteClick(index: number) {
        this.form.controls.additionalTimeSpans.removeAt(index);
    }

    @HostListener('window:beforeunload')
    // tslint:disable-next-line:naming-convention
    canDeactivate(): boolean {
        return !this.form.dirty;
    }
    closeDialog() {
        if (this.dialogRef) {
            if (this.canDeactivate() || confirm(PENDING_CHANGES_DEFAULT_MESSAGE)) {
                this.dialogRef.close();
            }
        }
    }
}
